import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDialogOpen, setSelectedEvent } from '../../store/Actions';
import { buildDetailPath, buildSectionPath, useAppNavigate } from '../../utils/Routing';
import {
  HeroBar, StatTile, YearChart,
  FriendRankRow, PlaceRankRow, EventRow,
} from './DetailViewParts';
import { EntityEvents } from './EntityEvents';
import {
  buildYearBreakdown, findMostActiveYear, rankByEvents,
  findTopPlace, buildRecentEvents, getEventStartAt,
  countDistinctPlaces, formatLongDate,
} from './insightHelpers';
import './DetailView.scss';

export function CategoryDetailView({entityId, tab = 'summary'}) {
  const categories = useSelector(state => state.viewingUserCategories) || {};
  const friends = useSelector(state => state.viewingUserFriends) || {};
  const trips = useSelector(state => state.viewingUserTrips) || {};
  const events = useSelector(state => state.viewingUserEvents) || {};
  const viewingUserAccount = useSelector(state => state.viewingUserAccount);
  const loggedInUserAccount = useSelector(state => state.loggedInUserAccount);
  const isOwnMap = viewingUserAccount && loggedInUserAccount
    && viewingUserAccount.id === loggedInUserAccount.id;
  const dispatch = useDispatch();
  const navigateTo = useAppNavigate();

  const category = categories[entityId];
  const username = viewingUserAccount?.username;

  useEffect(() => {
    if(!category && username){
      navigateTo(buildSectionPath(username, 'Categories'));
    }
  }, [category, username, navigateTo]);

  // Events with this category — root canvas for every insight.
  const categoryEvents = useMemo(() => {
    return Object.values(events).filter(e => e.categoryId === entityId);
  }, [events, entityId]);

  const yearEntries = useMemo(() => buildYearBreakdown(categoryEvents), [categoryEvents]);
  const mostActiveYear = useMemo(() => findMostActiveYear(categoryEvents), [categoryEvents]);
  const topFriends = useMemo(
    () => rankByEvents(categoryEvents, e => e.friendIds, friends),
    [categoryEvents, friends]
  );
  const topTrips = useMemo(
    () => rankByEvents(categoryEvents, e => e.tripIds, trips),
    [categoryEvents, trips]
  );
  const topPlace = useMemo(() => findTopPlace(categoryEvents), [categoryEvents]);
  const placeCount = useMemo(() => countDistinctPlaces(categoryEvents), [categoryEvents]);
  const recentEvents = useMemo(() => buildRecentEvents(categoryEvents, 6), [categoryEvents]);

  // Top places by count for this category.
  const topPlacesList = useMemo(() => {
    const counts = {};
    categoryEvents.forEach(event => {
      const description = event.location?.description;
      if(!description) return;
      counts[description] = (counts[description] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([label, count]) => ({label, count}))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [categoryEvents]);

  const sortedDates = useMemo(() => {
    return categoryEvents.map(getEventStartAt).filter(Boolean).sort((a, b) => a - b);
  }, [categoryEvents]);
  const firstDate = sortedDates[0] || null;
  const lastDate = sortedDates[sortedDates.length - 1] || null;
  const yearsCovered = firstDate && lastDate
    ? Math.max(1, lastDate.getFullYear() - firstDate.getFullYear() + 1)
    : 0;

  if(!category){
    return (
      <main className="detail-view">
        <div className="detail-view__not-found">
          <div>This category couldn't be found.</div>
        </div>
      </main>
    );
  }

  const accent = category.pinColor || '#10b981';

  const goBack = () => {
    if(!username) return;
    navigateTo(buildSectionPath(username, 'Categories'));
  };

  const selectTab = (nextTab) => {
    if(!username || nextTab === tab) return;
    navigateTo(buildDetailPath(username, 'Categories', category.id, nextTab));
  };

  const openEdit = () => {
    dispatch(setDialogOpen('Edit Category', {
      parentDialog: '',
      listDialog: null,
      dialogTitle: `Edit Category: ${category.name}`,
      category,
    }));
  };

  const openNewEvent = () => {
    if(!loggedInUserAccount) return;
    const newEvent = {
      userId: loggedInUserAccount.id,
      title: '',
      categoryId: category.id,
    };
    dispatch(setDialogOpen('Edit Event', {event: newEvent}));
  };

  const openEvent = (event) => {
    dispatch(setSelectedEvent(event));
    dispatch(setDialogOpen('View Event', {event}));
  };

  const openFriend = (friend) => {
    if(!username) return;
    navigateTo(`/${encodeURIComponent(username)}/friends/${encodeURIComponent(friend.id)}`);
  };

  const openTrip = (trip) => {
    if(!username) return;
    navigateTo(`/${encodeURIComponent(username)}/trips/${encodeURIComponent(trip.id)}`);
  };

  return (
    <main className={`detail-view ${tab === 'events' ? 'detail-view--events' : ''}`.trim()}>
      <section className={`detail-view__hero ${tab === 'events' ? 'detail-view__hero--collapsed' : ''}`.trim()}>
        <div
          className="detail-view__hero-glow detail-view__hero-glow--category-themed"
          style={{'--hero-accent': accent}}
          aria-hidden="true"
        />
        <HeroBar
          backLabel="All Categories"
          onBack={goBack}
          onNewEvent={isOwnMap ? openNewEvent : undefined}
          onEdit={isOwnMap ? openEdit : undefined}
          editLabel="Edit Category"
          entityTitle={category.name}
          tab={tab}
          onSelectTab={selectTab}
        />
        <div className="detail-view__hero-content">
          <div
            className="detail-view__hero-icon"
            style={{backgroundColor: accent, color: '#ffffff'}}
            aria-hidden="true"
          >
            {category.defaultEmoji}
          </div>
          <div className="detail-view__hero-tagline">Category</div>
          <h1 className="detail-view__hero-title">{category.name}</h1>
          <div className="detail-view__hero-subtitle">
            {categoryEvents.length > 0
              ? `${categoryEvents.length} ${categoryEvents.length === 1 ? 'event' : 'events'}${yearsCovered > 0 ? ` over ${yearsCovered} ${yearsCovered === 1 ? 'year' : 'years'}` : ''}`
              : 'No events in this category yet'}
          </div>
          <div className="detail-view__hero-meta-row">
            <span className="detail-view__chip">
              <span className="detail-view__chip-dot" style={{'--chip-color': accent}}/>
              {accent.toUpperCase()}
            </span>
            <span className="detail-view__chip">
              {category.defaultEmoji} default emoji
            </span>
          </div>
        </div>
      </section>

      {tab === 'events' &&
        <EntityEvents
          events={categoryEvents}
          emptyText={`Once you tag events with ${category.name}, they'll show up here.`}
        />
      }

      {tab === 'summary' &&
      <>
      <section className="detail-view__stats-grid" aria-label="Category stats">
        <StatTile
          label="Events"
          value={categoryEvents.length}
          accent={accent}
          icon="📅"
          delay={120}
        />
        <StatTile
          label="Places"
          value={placeCount}
          accent="#0ea5e9"
          icon="📍"
          delay={200}
        />
        <StatTile
          label="Friends"
          value={topFriends.length}
          accent="#f59e0b"
          icon="👥"
          delay={280}
        />
        <StatTile
          label="Trips"
          value={topTrips.length}
          accent="#8b5cf6"
          icon="🧳"
          delay={360}
        />
      </section>

      {yearEntries.length > 0 &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Events by Year</h2>
            {mostActiveYear && mostActiveYear[1] > 0 &&
              <span className="detail-view__panel-subtitle">
                {mostActiveYear[0]} was the busiest year ({mostActiveYear[1]} {mostActiveYear[1] === 1 ? 'event' : 'events'})
              </span>
            }
          </header>
          <YearChart entries={yearEntries} accentTop={accent} accentBottom={accent}/>
        </section>
      }

      {(topPlacesList.length > 0 || topFriends.length > 0) &&
        <section className="detail-view__two-up">
          {topPlacesList.length > 0 &&
            <div className="detail-view__panel">
              <header className="detail-view__panel-header">
                <h2>Top Places</h2>
              </header>
              <div className="detail-view__rank-list">
                {topPlacesList.map((item, idx) => (
                  <PlaceRankRow
                    key={`${item.label}-${idx}`}
                    label={item.label}
                    count={item.count}
                    percent={(item.count / topPlacesList[0].count) * 100}
                    delay={400 + idx * 80}
                  />
                ))}
              </div>
            </div>
          }
          {topFriends.length > 0 &&
            <div className="detail-view__panel">
              <header className="detail-view__panel-header">
                <h2>Top Friends</h2>
              </header>
              <div className="detail-view__rank-list">
                {topFriends.map((item, idx) => (
                  <FriendRankRow
                    key={item.entity.id}
                    friend={item.entity}
                    count={item.count}
                    percent={(item.count / topFriends[0].count) * 100}
                    delay={500 + idx * 80}
                    onClick={() => openFriend(item.entity)}
                  />
                ))}
              </div>
            </div>
          }
        </section>
      }

      {(topTrips.length > 0 || firstDate || topPlace) &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Highlights</h2>
          </header>
          <div className="detail-view__rank-list">
            {firstDate &&
              <div className="detail-view__rank-row">
                <div className="detail-view__rank-icon" style={{backgroundColor:'#10b981'}}>✨</div>
                <div className="detail-view__rank-body">
                  <div className="detail-view__rank-label-row">
                    <span className="detail-view__rank-name">First {category.name.toLowerCase()} event</span>
                    <span className="detail-view__rank-count">{formatLongDate(firstDate)}</span>
                  </div>
                </div>
              </div>
            }
            {lastDate && lastDate.getTime() !== firstDate?.getTime() &&
              <div className="detail-view__rank-row">
                <div className="detail-view__rank-icon" style={{backgroundColor:'#f59e0b'}}>🕒</div>
                <div className="detail-view__rank-body">
                  <div className="detail-view__rank-label-row">
                    <span className="detail-view__rank-name">Most recent</span>
                    <span className="detail-view__rank-count">{formatLongDate(lastDate)}</span>
                  </div>
                </div>
              </div>
            }
            {topTrips.length > 0 &&
              <button
                type="button"
                className="detail-view__rank-row detail-view__rank-row--clickable"
                onClick={() => openTrip(topTrips[0].entity)}
              >
                <div className="detail-view__rank-icon" style={{backgroundColor:'#0ea5e9'}}>🧳</div>
                <div className="detail-view__rank-body">
                  <div className="detail-view__rank-label-row">
                    <span className="detail-view__rank-name">Top trip</span>
                    <span className="detail-view__rank-count">{topTrips[0].count}</span>
                  </div>
                  <div className="detail-view__event-meta" style={{marginTop:0}}>
                    {topTrips[0].entity.name}
                  </div>
                </div>
              </button>
            }
          </div>
        </section>
      }

      {recentEvents.length > 0 &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Recent Events</h2>
            <span className="detail-view__panel-subtitle">
              {categoryEvents.length} total
            </span>
          </header>
          <div className="detail-view__events-list">
            {recentEvents.map((item, idx) => (
              <EventRow
                key={item.event.id}
                event={item.event}
                startAt={item.startAt}
                category={category}
                onClick={() => openEvent(item.event)}
                delay={120 + idx * 50}
              />
            ))}
          </div>
        </section>
      }

      {categoryEvents.length === 0 &&
        <section className="detail-view__empty">
          <div className="detail-view__empty-title">No events in this category yet</div>
          <div>Once you tag events with {category.name}, they'll show up here.</div>
        </section>
      }
      </>
      }
    </main>
  );
}
